import React, { useState, useEffect } from 'react';
import './LogDetailModal.css';
import FlightLogMap from './FlightLogMap';
import { getCityName } from '../utils/geocoding';
import { LogType } from '../interfaces/LogType';
import { SettingsType, UserType } from '../interfaces/SettingsType';
import ConfirmDialog from './ConfirmDialog';
import { getWeatherIcon } from '../utils/weatherIcons';
import { getWeatherTranslation } from '../utils/weatherTranslate';
import ExportCSVButton from './ExportCSVButton';
import { saveLogs } from '../firebase/db-logs';

interface FlightLogEntry {
  height: number;
  latitude: number;
  longitude: number;
  mode: string;
  pitch: number;
  roll: number;
  yaw: number;
}

interface LogModalProps {
  settings: SettingsType;
  log: LogType;
  logNumber: number;
  pilots: UserType[];
  onClose: (val: LogType | undefined) => void;
  isAdmin: boolean;
}

const LogDetailModal: React.FC<LogModalProps> = ({
  settings,
  log,
  logNumber,
  pilots,
  onClose,
  isAdmin,
}) => {
  const [allowEdit, setAllowEdit] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [currentLog, setCurrentLog] = useState(log);
  const [selectedPilot, setSelectedPilot] = useState(log.pilotId || 'N/A');
  const [selectedObserver, setSelectedObserver] = useState(
    log.observerId || 'N/A'
  );
  const [selectedAnalyst, setSelectedAnalyst] = useState(
    log.analystId || 'N/A'
  );
  const [selectedTyp, setSelectedTyp] = useState(log.mission || 'N/A');
  const [selectedFlightType, setSelectedFlightType] = useState(
    log.flightType || 'N/A'
  );
  const [remarks, setRemarks] = useState(log.location || '');
  const [cityName, setCityName] = useState<string>('');
  const [weatherData, setWeatherData] = useState<any>(null);

  const [activeTab, setActiveTab] = useState<
    'flugdaten' | 'wetterdaten' | 'flugkarte'
  >('flugdaten');
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  console.log(log);
  const flightLogDataTyped = log.flight as FlightLogEntry[];
  const firstCoordinate = flightLogDataTyped[0];

  useEffect(() => {
    async function fetchCityName() {
      if (
        firstCoordinate &&
        firstCoordinate.latitude &&
        firstCoordinate.longitude
      ) {
        const city = await getCityName(
          firstCoordinate.latitude,
          firstCoordinate.longitude
        );
        setCityName(city);
      } else {
        setCityName('Ort unbekannt');
      }
    }
    fetchCityName();

    const twentyFourHours = 24 * 60 * 60 * 1000;
    const timestamp = new Date().getTime() - twentyFourHours;
    const logTime = new Date(log.logCreated).getTime();
    if (isAdmin || timestamp < logTime) {
      setAllowEdit(true);
    }
  }, [firstCoordinate, log.startTime, isAdmin]);

  useEffect(() => {
    async function fetchWeatherData() {
      const updatedLogs: LogType[] = [];
      if(log.weatherData != undefined){
        setWeatherData(log.weatherData)
        console.log(log.weatherData)
        console.log("Aus Datenbank")
      } else if (
        firstCoordinate &&
        firstCoordinate.latitude &&
        firstCoordinate.longitude &&
        log.startTime
      ) {
        try {
          const apiKey =
            process.env.REACT_APP_WEATHER_API_KEY ||
            '7c5f2e61b154c685c0a4f743cdff226e';
          const lat = firstCoordinate.latitude;
          const lon = firstCoordinate.longitude;
          const timestamp = Math.floor(
            new Date(log.startTime).getTime() / 1000
          );
          const url = `https://api.openweathermap.org/data/3.0/onecall/timemachine?lat=${lat}&lon=${lon}&dt=${timestamp}&appid=${apiKey}&units=metric`;
          const response = await fetch(url);
          const data = await response.json();
          setWeatherData(data);
          log.weatherData = data
          updatedLogs.push(log)
          if (updatedLogs.length > 0) {
                  try {
                    await saveLogs(updatedLogs);
                    console.log('Logs erfolgreich aktualisiert');
                  } catch (error) {
                    console.error(
                      'Fehler beim Speichern der aktualisierten Logs:',
                      error
                    );
                  }
                }
        } catch (error) {
          console.error('Fehler beim Laden der Wetterdaten:', error);
        }
      }
    }
    fetchWeatherData();
  }, [firstCoordinate, log.startTime]);

  const handlePilotChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedPilot(event.target.value);
    setCurrentLog({ ...currentLog, pilotId: event.target.value });
  };
  const handleMissionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedTyp(event.target.value);
    setCurrentLog({ ...currentLog, mission: event.target.value });
  };
  const handleTypChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedFlightType(event.target.value);
    setCurrentLog({ ...currentLog, flightType: event.target.value });
  };
  const handleCommentChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setCurrentLog({ ...currentLog, comment: event.target.value });
  };
  const handleObserverChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSelectedObserver(event.target.value);
    setCurrentLog({ ...currentLog, observerId: event.target.value });
  };
  const handleAnalystChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedAnalyst(event.target.value);
    setCurrentLog({ ...currentLog, analystId: event.target.value });
  };

  const [isOpen, setIsOpen] = useState(false);

  const toggleOpen = () => {
    setIsOpen(!isOpen);
  };

  const calculateDuration = (startTime: string, endTime: string) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationSeconds = Math.floor(
      (end.getTime() - start.getTime()) / 1000
    );
    const minutes = Math.floor(durationSeconds / 60);
    const seconds = durationSeconds % 60;
    return `${minutes}m ${seconds}s`;
  };

  // Inhalt für Flugdaten (linke Seite)
  const flugdatenContent = (
    <div className="modal-left">
      <div className="modal-details">
        <div className="modal-group">
          <div>
            <strong>Flug Nr.:</strong> {logNumber}
          </div>
          <div>
            <strong>Datum:</strong>
            <input
              className="modal-input modal-input-small"
              value={new Date(currentLog.startTime).toLocaleDateString()}
              onChange={() => alert('Datum darf nicht geändert werden')}
            />
          </div>
          <div>
            <strong>Ort:</strong>
            <input
              className="modal-input modal-input-small"
              value={currentLog.location}
              onChange={(e) => {
                if (allowEdit) {
                  setCurrentLog({
                    ...currentLog,
                    location: e.currentTarget.value,
                  });
                }
              }}
            />
          </div>
        </div>
        <div className="modal-group">
          <div>
            <strong>Abflug:</strong>{' '}
            {new Date(currentLog.startTime).toLocaleTimeString()}
          </div>
          <div>
            <strong>Landung:</strong>{' '}
            {new Date(currentLog.endTime).toLocaleTimeString()}
          </div>
          <div>
            <strong>Flugdauer:</strong>{' '}
            {calculateDuration(currentLog.startTime, currentLog.endTime)}
          </div>
          <div
            className="hover"
            onClick={() => {
              if (allowEdit) {
                setCurrentLog({ ...currentLog, crash: !currentLog.crash });
              }
            }}
          >
            <strong>Unfall:</strong> {currentLog.crash ? 'Ja' : 'Nein'}
          </div>
        </div>
        <div className="modal-group modal-group-full">
          <div>
            <strong>Pilot:</strong>
            <select value={selectedPilot} onChange={handlePilotChange}>
              {pilots.map((pilot) => (
                <option key={pilot.email} value={pilot.email}>
                  {pilot.prename + ' ' + pilot.lastname}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="modal-group modal-group-full">
          <div>
            <strong>Luftraumbeobachter:</strong>
            <select value={selectedObserver} onChange={handleObserverChange}>
              {pilots.map((pilot) => (
                <option key={pilot.email} value={pilot.email}>
                  {pilot.prename + ' ' + pilot.lastname}
                </option>
              ))}
            </select>
          </div>
          <div>
            <strong>Bildauswerter:</strong>
            <select value={selectedAnalyst} onChange={handleAnalystChange}>
              {pilots.map((pilot) => (
                <option key={pilot.email} value={pilot.email}>
                  {pilot.prename + ' ' + pilot.lastname}
                </option>
              ))}
            </select>
          </div>
          <div>
            <strong>Art:</strong>
            <select value={currentLog.mission} onChange={handleMissionChange}>
              <option value="Einsatz">Einsatz</option>
              <option value="Event">Event</option>
              <option value="Übung">Übung</option>
            </select>
          </div>
          <div>
            <strong>Flugart:</strong>
            <select value={currentLog.flightType} onChange={handleTypChange}>
              <option value="BVLOS">BVLOS</option>
              <option value="EVLOS">EVLOS</option>
              <option value="VLOS">VLOS</option>
            </select>
          </div>
          <div className="modal-group-full">
            <strong>Bemerkungen:</strong>
            <textarea
              className="remarks-input"
              rows={4}
              value={currentLog.comment}
              onChange={handleCommentChange}
            />
          </div>
        </div>
        <div className="last-edit">
          <p
            onClick={toggleOpen}
            style={{
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            Bearbeitungslog
          </p>

          {isOpen && (
            <p>
              Bearbeitet von:
              {currentLog.userLastEdits.map((user, index) => (
                <React.Fragment key={index}>
                  <br />
                  {user}
                </React.Fragment>
              ))}
            </p>
          )}
        </div>
      </div>
    </div>
  );

  const wetterdatenContent = (
    <div>
      {weatherData && weatherData.data && weatherData.data.length > 0 ? (
        (() => {
          const item = weatherData.data[0];
          const isNight = item.dt < item.sunrise || item.dt > item.sunset;

          return (
            <div>
              <div className="weather-container">
                <div className="weather-main">
                  <span className="material-icons weather-icon">
                    {getWeatherIcon(item.weather[0].main)}
                  </span>
                  <div className="weather-temp">{Math.ceil(item.temp)}°C</div>
                </div>
                <div className="weather-details">
                  <div className="weather-detail">
                    <span className="material-icons">air</span>
                    <span>{item.wind_speed} m/s</span>
                  </div>
                  <div className="weather-detail">
                    <span className="material-icons">opacity</span>
                    <span>{item.humidity}%</span>
                  </div>
                  <div className="weather-detail">
                    <span className="material-icons">visibility</span>
                    <span>
                      {item.visibility}
                      <span className="unit"> m</span>
                    </span>
                  </div>
                  <div className="weather-detail">
                    <span className="material-icons">brightness_6</span>
                    <span>{isNight ? 'Dunkel' : 'Hell'}</span>
                  </div>
                  <div className="weather-detail">
                    <span className="material-icons">cloud</span>
                    <span>
                      {getWeatherTranslation(item.weather[0].description)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="map-container" style={{ height: '640px' }}>
                <FlightLogMap
                  flightData={flightLogDataTyped.map((entry) => ({
                    lat: entry.latitude,
                    lng: entry.longitude,
                    height: entry.height,
                  }))}
                  visible={activeTab === 'flugkarte'}
                />
              </div>
            </div>
          );
        })()
      ) : (
        <p>Lade Wetterdaten...</p>
      )}
    </div>
  );

  return (
    <div className="modal-overlay" onClick={() => onClose(log)}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="button-container">
          {isAdmin && (
            <>
              <button
                className="modal-button material-icons delete-button"
                onClick={() => setShowConfirm(true)}
              >
                delete
              </button>
              {showConfirm && (
                <ConfirmDialog
                  message="Willst du dieses Log wirklich unwiederuflich löschen?"
                  onConfirm={() => onClose(undefined)}
                  onCancel={() => setShowConfirm(false)}
                />
              )}
            </>
          )}
          {allowEdit && (
            <>
              <ExportCSVButton data={[currentLog]} />
              <button
                className="modal-button material-icons save-button-modal"
                onClick={() => {
                  currentLog.userLastEdits.push(
                    `${settings?.prename} ${
                      settings?.lastname
                    } - ${new Date().toLocaleString()}`
                  );
                  onClose(currentLog);
                }}
              >
                save
              </button>
            </>
          )}
          <button
            className="modal-button material-icons close-button"
            onClick={() => onClose(log)}
          >
            close
          </button>
        </div>

        <div className="modal-header">
          <h2>Flugdetails</h2>
        </div>

        {isSmallScreen ? (
          <>
            <div className="modal-tabs">
              <button
                className={activeTab === 'flugdaten' ? 'active' : ''}
                onClick={() => setActiveTab('flugdaten')}
              >
                Flugdaten
              </button>
              <button
                className={activeTab === 'wetterdaten' ? 'active' : ''}
                onClick={() => setActiveTab('wetterdaten')}
              >
                Karte & Wetter
              </button>
            </div>
            <div className="modal-tab-content">
              {activeTab === 'flugdaten' && flugdatenContent}
              {activeTab === 'wetterdaten' && wetterdatenContent}
            </div>
          </>
        ) : (
          <div className="modal-body">
            <div className="modal-left">{flugdatenContent}</div>
            <div className="modal-right-logdetail">
              <div className="weather-widget-smallScreen">
                {weatherData &&
                weatherData.data &&
                weatherData.data.length > 0 ? (
                  (() => {
                    const item = weatherData.data[0];
                    const isNight =
                      item.dt < item.sunrise || item.dt > item.sunset;
                    return (
                      <div className="weather-widget">
                        {weatherData &&
                        weatherData.data &&
                        weatherData.data.length > 0 ? (
                          (() => {
                            const item = weatherData.data[0];
                            const isNight =
                              item.dt < item.sunrise || item.dt > item.sunset;

                            return (
                              <div className="weather-container">
                                <div className="weather-main">
                                  <span className="material-icons weather-icon">
                                    {getWeatherIcon(item.weather[0].main)}
                                  </span>
                                  <div className="weather-temp">
                                    {Math.ceil(item.temp)}°C
                                  </div>
                                </div>
                                <div className="weather-details">
                                  <div className="weather-detail">
                                    <span className="material-icons">air</span>
                                    <span>{item.wind_speed} m/s</span>
                                  </div>
                                  <div className="weather-detail">
                                    <span className="material-icons">
                                      opacity
                                    </span>
                                    <span>{item.humidity}%</span>
                                  </div>
                                  <div className="weather-detail">
                                    <span className="material-icons">
                                      visibility
                                    </span>
                                    <span>{item.visibility} m</span>
                                  </div>
                                  <div className="weather-detail">
                                    <span className="material-icons">
                                      brightness_6
                                    </span>
                                    <span>{isNight ? 'Dunkel' : 'Hell'}</span>
                                  </div>
                                  <div className="weather-detail">
                                    <span className="material-icons">
                                      cloud
                                    </span>
                                    <span>
                                      {getWeatherTranslation(
                                        item.weather[0].description
                                      )}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })()
                        ) : (
                          <p>Lade Wetterdaten...</p>
                        )}
                      </div>
                    );
                  })()
                ) : (
                  <p>Lade Wetterdaten...</p>
                )}
              </div>
              <div className="map-container">
                <FlightLogMap
                  flightData={flightLogDataTyped.map((entry) => ({
                    lat: entry.latitude,
                    lng: entry.longitude,
                    height: entry.height,
                  }))}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LogDetailModal